import { useMemo } from 'react'

import { BidType, FCRD_MARKET_PROGRAMS, MFRR_MARKET_PROGRAMS } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'

export type BidOfferDataRequirements = {
  /**
   * Whether capacity bid data is needed.
   * Required when selected country is Finland with MFRR UP/DOWN energy bids.
   */
  requiresCapacityBids: boolean

  /**
   * Whether latest non-draft bid data is needed.
   * Can be disabled if we don't need to compare with previous bids.
   */
  requiresLatestNonDraftBids: boolean

  /**
   * Whether bid basis data is needed.
   * Can be disabled for certain market programs or portfolio combinations.
   */
  requiresBidBasis: boolean

  /**
   * Whether to filter activation groups to only C_AND_I resource category.
   * Enabled when ALL the following conditions are met for MFRR:
   * - Cascading bid page feature toggle is enabled
   * - Selected country is Finland (fi)
   * - Market program is MFRR UP or MFRR DOWN
   * - Bid type is CAPACITY
   * OR for FCR-D:
   * - FCR-D cascading feature toggle is enabled
   * - Selected country is Finland (fi)
   * - Market program is FCR-D UP Dynamic or FCR-D DOWN Dynamic
   * - Bid type is CAPACITY
   */
  requiresOnlyCAndIActivationGroups: boolean
}

/**
 * Hook that determines what data needs to be fetched based on groupParams.
 * This centralizes the logic for conditional data loading, making it explicit
 * what data is required for different scenarios.
 *
 * @param groupParams - The grouped bid parameters
 * @returns An object indicating which data sets are required
 *
 * @example
 * ```tsx
 * const requirements = useBidOfferDataRequirements(groupParams)
 * const capacityBids = useBidOfferCapacityBids(groupParams, { enabled: requirements.requiresCapacityBids })
 * ```
 */
export function useBidOfferDataRequirements(groupParams: GroupedBidParams): BidOfferDataRequirements {
  const { isEnabled } = useFeatureToggle()
  const { selectedCountry } = useBiddingContext()

  return useMemo(() => {
    // Capacity bids are only needed when selected country is one of Nordics with MFRR energy bids
    const requiresCapacityBids =
      (selectedCountry === 'fi' || selectedCountry === 'no' || selectedCountry === 'se') &&
      MFRR_MARKET_PROGRAMS.includes(groupParams.marketProgram) &&
      groupParams.bidType === BidType.ENERGY

    // Latest non-draft bids are always needed for now
    // This could be made conditional in the future based on requirements
    const requiresLatestNonDraftBids = true

    // Bid basis is always needed for now
    // This could be made conditional in the future based on requirements
    const requiresBidBasis = true

    // Filter to only C_AND_I activation groups when cascading is enabled for MFRR or FCR-D
    const isMfrrCascading =
      isEnabled('FLEX_PORTAL_BID_CASCADING_MFRR_ENABLED') &&
      selectedCountry === 'fi' &&
      MFRR_MARKET_PROGRAMS.includes(groupParams.marketProgram) &&
      groupParams.bidType === BidType.CAPACITY

    const isFcrdCascading =
      isEnabled('FLEX_PORTAL_BID_CASCADING_FCRD_ENABLED') &&
      selectedCountry === 'fi' &&
      FCRD_MARKET_PROGRAMS.includes(groupParams.marketProgram) &&
      groupParams.bidType === BidType.CAPACITY

    const requiresOnlyCAndIActivationGroups = isMfrrCascading || isFcrdCascading

    return {
      requiresCapacityBids,
      requiresLatestNonDraftBids,
      requiresBidBasis,
      requiresOnlyCAndIActivationGroups,
    }
  }, [groupParams, isEnabled, selectedCountry])
}
